import { useFrame } from '@react-three/fiber';
import { memo, useMemo, useRef } from 'react';
import { Color, Vector3 } from 'three';
import type { Group } from 'three';
import StudioEnvironment from './Environment';
import { STUDIO_KEY_LIGHT_POSITION } from './config';

const BASE_CYCLORAMA_BOUNCE_BLEND = 0.4;
const MAX_CYCLORAMA_BOUNCE_BLEND = 1;
const BASE_CYCLORAMA_BOUNCE_INTENSITY = 0.3;
const MAX_CYCLORAMA_BOUNCE_INTENSITY = 2.5;
const CYCLORAMA_BOUNCE_BOOST_EXPONENT = 4;
const STUDIO_GROUND_COLOR = '#cbd2dc';

interface Props {
    ambientLightIntensity: number
    bounceStrength: number
    cycloramaColor: string
    directLightIntensity: number
    reflectionStrength: number
}

/**
 * Camera-relative direct-light rig. The hemisphere ground color approximates
 * diffuse cyclorama bounce; it is intentionally an art-directed effect rather
 * than illumination computed from the backdrop geometry.
 */
export default memo(function StudioLights({
    ambientLightIntensity,
    bounceStrength,
    cycloramaColor,
    directLightIntensity,
    reflectionStrength,
}:Props) {
    const lightRig = useRef<Group>(null);
    const boostedBounceStrength = bounceStrength ** CYCLORAMA_BOUNCE_BOOST_EXPONENT;
    const bounceColorBlend = bounceStrength * BASE_CYCLORAMA_BOUNCE_BLEND
        + boostedBounceStrength * (
            MAX_CYCLORAMA_BOUNCE_BLEND - BASE_CYCLORAMA_BOUNCE_BLEND
        );
    const bounceIntensity = BASE_CYCLORAMA_BOUNCE_INTENSITY
        + boostedBounceStrength * (
            MAX_CYCLORAMA_BOUNCE_INTENSITY - BASE_CYCLORAMA_BOUNCE_INTENSITY
        );
    const bounceGroundColor = useMemo(() => (
        new Color(STUDIO_GROUND_COLOR).lerp(
            new Color(cycloramaColor),
            bounceColorBlend,
        )
    ), [bounceColorBlend, cycloramaColor]);
    const cameraDirection = useMemo(() => new Vector3(), []);

    useFrame(({camera, scene}) => {
        // Keep direct lights and reflections aligned with the camera so front
        // and back previews receive the same relative studio rig.
        camera.getWorldDirection(cameraDirection);
        const cameraYaw = Math.atan2(
            -cameraDirection.x,
            -cameraDirection.z,
        );
        if(lightRig.current) {
            lightRig.current.rotation.y = cameraYaw;
        }
        // Apply this after cubemap generation so slider updates do not trigger
        // an expensive environment recapture.
        scene.environmentIntensity = ambientLightIntensity;
        scene.environmentRotation.y = cameraYaw;
    }, -1);

    return <>
        <hemisphereLight
            color="#fffdf8"
            groundColor={bounceGroundColor}
            intensity={bounceIntensity * ambientLightIntensity}
        />
        <group ref={lightRig}>
            <directionalLight
                color="#fff6e9"
                intensity={1.25 * directLightIntensity}
                position={STUDIO_KEY_LIGHT_POSITION}
            />
            <directionalLight
                color="#e8f1ff"
                intensity={0.35 * directLightIntensity}
                position={[4, 2, 4]}
            />
            <directionalLight
                color="#fff9f0"
                intensity={0.55 * directLightIntensity}
                position={[2, 5, -5]}
            />
        </group>
        <StudioEnvironment
            cycloramaColor={cycloramaColor}
            reflectionStrength={reflectionStrength}
        />
    </>;
});
